/**
 * Builds the `ml_lens` envelope for the Quick Find post-query `taskSuccess`
 * event, attaching per-product result counts in an ML-Lens-compatible shape.
 *
 * See: https://hello.atlassian.net/wiki/spaces/~93171358/pages/6820686403/
 *   GASv3+Events+Transformation+for+ML+Lens+Metrics+Schema
 *
 * Used by the `quick-find/postquery-search-results-ux` `taskSuccess` event.
 *
 * For `POSTQUERY_NO_RESULTS`, callers pass an empty `productCounts` object,
 * which yields no metrics — so no envelope is emitted and `ml_lens` is omitted
 * from the event entirely. An envelope carrying `"metrics":[]` invalidates the
 * whole `ml_lens` payload for the transformer, so "post-query succeeded with
 * zero results" is signalled by the absence of `ml_lens` rather than by an
 * empty metric list.
 *
 * This is a thin, surface-specific wrapper around the generic
 * {@link buildMlLensEnvelope}.
 */

import { buildMlLensEnvelope, type MlLensEnvelope, type MlLensMetric } from './ml-lens-envelope';

export const POST_QUERY_ML_LENS_NAMESPACE = 'rovo.quick_find';

export const POST_QUERY_ML_LENS_BASE_TAGS = {
	page: 'quick_find',
	task: 'postquery_search_results_ux',
} as const;

export const POST_QUERY_RESULTS_PER_CONNECTOR_METRIC_KEY = 'results_per_connector';

/**
 * Build the `ml_lens` envelope for the post-query `taskSuccess` event.
 *
 * @param productCounts Map of `data-product` → number of results rendered for
 *                      that product (as produced by `getProductResultCounts`).
 *                      Must be non-empty: `{}` produces zero metrics, so the
 *                      builder returns `null` (no envelope). Note a product
 *                      with an explicit count of `0` still produces a metric,
 *                      so `{ confluence: 0 }` is emitted.
 * @param userId        Current user id; added to metric-set tags as `user_id`.
 *                      Required: returns `null` (no envelope) when
 *                      null/undefined/empty.
 */
export const buildPostQueryMlLensEnvelope = (
	productCounts: Record<string, number>,
	userId?: string | null,
): MlLensEnvelope | null => {
	const metrics: MlLensMetric[] = Object.entries(productCounts)
		// Stable ordering for deterministic payloads / easier diffing in QA.
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([connector, value]) => ({
			key: POST_QUERY_RESULTS_PER_CONNECTOR_METRIC_KEY,
			type: 'number',
			value,
			tags: { connector },
		}));

	return buildMlLensEnvelope({
		namespace: POST_QUERY_ML_LENS_NAMESPACE,
		baseTags: { ...POST_QUERY_ML_LENS_BASE_TAGS },
		userId,
		metrics,
	});
};
